/**
 * templates.ts (MKT-REDESIGN-01 M3, 2026-09-03) — the marketing template
 * gallery's data: templates (with archived), versions, rename / duplicate /
 * archive / restore-a-version, and the AI first draft (draft-email function:
 * the model writes the words, Pegasus owns the on-brand layout).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface MarketingTemplate {
  id: string; key: string; name: string; subject: string | null; kind: string; html: string | null;
  version: number; archived: boolean; updated_by: string | null; updated_at: string | null;
}
export interface TemplateVersion { id: string; template_id: string; version: number; subject: string | null; html: string; saved_by: string | null; saved_at: string }

export const TEMPLATES_KEY = ["marketing_templates"] as const;

export function useMarketingTemplates(includeArchived = false) {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, includeArchived],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<MarketingTemplate[]> => {
      let q = supabase.from("pegasus_template").select("id, key, name, subject, kind, html, version, archived, updated_by, updated_at").eq("kind", "marketing");
      if (!includeArchived) q = q.eq("archived", false);
      const { data, error } = await q.order("archived").order("name");
      if (error) throw error;
      return (data ?? []) as MarketingTemplate[];
    },
  });
}

export function useTemplateVersions(templateId: string | null) {
  return useQuery({
    queryKey: ["template_versions", templateId],
    enabled: !!templateId,
    queryFn: async (): Promise<TemplateVersion[]> => {
      const { data, error } = await supabase.from("pegasus_template_version").select("id, template_id, version, subject, html, saved_by, saved_at")
        .eq("template_id", templateId!).order("version", { ascending: false }).limit(30);
      if (error) throw error;
      return (data ?? []) as TemplateVersion[];
    },
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
  qc.invalidateQueries({ queryKey: ["template_versions"] });
}

export function useRenameTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; name: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_template").update({ name: v.name.trim(), updated_by: u.user?.email ?? null, updated_at: new Date().toISOString() }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useArchiveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; archived: boolean }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_template").update({ archived: v.archived, updated_by: u.user?.email ?? null, updated_at: new Date().toISOString() }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateAll(qc),
  });
}

/** Copy a template (html + design) to a new key so it can be edited without touching the original. */
export function useDuplicateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<MarketingTemplate> => {
      const { data: src, error } = await supabase.from("pegasus_template").select("*").eq("id", id).single();
      if (error) throw error;
      const { data: u } = await supabase.auth.getUser();
      const s = src as MarketingTemplate & { design_json: unknown; merge_schema: unknown; text_fallback: string | null };
      const key = `${s.key.replace(/_copy\d*$/, "")}_copy${Date.now().toString().slice(-5)}`;
      const { data: ins, error: ie } = await supabase.from("pegasus_template").insert({
        key, name: `${s.name} (copy)`, subject: s.subject, kind: s.kind, html: s.html, text_fallback: s.text_fallback ?? null,
        design_json: s.design_json ?? null, merge_schema: s.merge_schema ?? {}, version: 1, archived: false,
        updated_by: u.user?.email ?? null, updated_at: new Date().toISOString(),
      }).select("id, key, name, subject, kind, html, version, archived, updated_by, updated_at").single();
      if (ie) throw ie;
      return ins as MarketingTemplate;
    },
    onSuccess: () => invalidateAll(qc),
  });
}

/** Put an older version back as the current one (a new version, so nothing is lost). */
export function useRestoreVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { templateId: string; version: TemplateVersion }) => {
      const { data: u } = await supabase.auth.getUser();
      const { data: cur, error } = await supabase.from("pegasus_template").select("version").eq("id", v.templateId).single();
      if (error) throw error;
      const next = ((cur as { version: number }).version ?? 0) + 1;
      const { data: full } = await supabase.from("pegasus_template_version").select("design_json").eq("id", v.version.id).maybeSingle();
      const now = new Date().toISOString();
      const { error: ue } = await supabase.from("pegasus_template").update({
        html: v.version.html, subject: v.version.subject, design_json: (full as { design_json: unknown } | null)?.design_json ?? null,
        version: next, updated_by: u.user?.email ?? null, updated_at: now,
      }).eq("id", v.templateId);
      if (ue) throw ue;
      await supabase.from("pegasus_template_version").insert({
        template_id: v.templateId, version: next, subject: v.version.subject, html: v.version.html,
        design_json: (full as { design_json: unknown } | null)?.design_json ?? null, saved_by: u.user?.email ?? null, saved_at: now,
      });
    },
    onSuccess: () => invalidateAll(qc),
  });
}

/** AI first draft → a new template (html only; the builder seeds its design from it). */
export function useDraftEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { brief: string; audience?: string; tone?: string; name?: string }): Promise<MarketingTemplate> => {
      const { data, error } = await supabase.functions.invoke("draft-email", { body: { brief: v.brief, audience: v.audience ?? "", tone: v.tone ?? "warm" } });
      if (error) throw error;
      const r = data as { ok?: boolean; subject?: string; html?: string; error?: string } | null;
      if (!r?.ok || !r.html) throw new Error(r?.error || "The draft didn't come back.");
      const { data: u } = await supabase.auth.getUser();
      const name = (v.name?.trim() || v.brief.trim().slice(0, 60)).replace(/\s+/g, " ");
      const key = `email_${Date.now()}`;
      const { data: ins, error: ie } = await supabase.from("pegasus_template").insert({
        key, name, subject: r.subject ?? null, kind: "marketing", html: r.html, design_json: null, merge_schema: {}, version: 1, archived: false,
        updated_by: u.user?.email ?? null, updated_at: new Date().toISOString(),
      }).select("id, key, name, subject, kind, html, version, archived, updated_by, updated_at").single();
      if (ie) throw ie;
      return ins as MarketingTemplate;
    },
    onSuccess: () => invalidateAll(qc),
  });
}

/** Sample values so a thumbnail reads like a real email, never like {{tokens}}. */
const SAMPLE_TOKENS: Record<string, string> = {
  first_name: "Sarah", last_name: "Lindqvist", full_name: "Sarah Lindqvist", service_address: "1240 Trapp Road", address: "1240 Trapp Road",
  city: "Eagan", account_no: "0012345", plan_name: "Plus Plan", renewal_date: "October 15", amount_due: "$189.00", region_window: "Sept 15–19",
  invoice_no: "125720", statement_date: "September 3",
};
export function sampleFill(html: string): string {
  return html.replace(/\{\{\s*([a-z_\.]+)\s*\}\}/gi, (_m, k: string) => SAMPLE_TOKENS[k] ?? (k.endsWith("_url") ? "#" : ""));
}
